/**
 * Limpiar Duplicados de Miembros
 *
 * Busca miembros duplicados (mismo correo en más de un documento)
 * en las colecciones asesor y asociado. Permite eliminar los duplicados
 * manteniendo el perfil correcto (el que tiene más datos/propiedades).
 */

import React, { useState, useCallback } from 'react';
import {
  collection,
  getDocs,
  doc,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import { propagarMiembroOficial } from '../../utils/propagarMiembroOficial';
// Sprint 3: herramientas destructivas pasan por snapshot + hardDelete + audit.
import { snapshotCollectionDocs } from '../../utils/snapshotBeforeOp';
import { hardDelete } from '../../utils/softDelete';
import { confirmDestructive } from '../../utils/confirmDestructive';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface MemberDoc {
  docId: string;
  collection: 'asesor' | 'asociado';
  nombre: string;
  correo: string;
  miembro_oficial?: boolean;
  firebase_uid?: string;
  hasUid: boolean;
  propsLinked: number; // count of asesor_doc_ids references
  createdAt?: any;
}

interface DuplicateGroup {
  correo: string;
  docs: MemberDoc[];
  keepDocId: string; // the one to keep (auto-selected: most data)
}

export default function LimpiarDuplicados() {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [scanned, setScanned] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);

  const addLog = (msg: string) => setLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);

  // ============ SCAN FOR DUPLICATES ============

  const buscarDuplicados = useCallback(async () => {
    setLoading(true);
    setDuplicates([]);
    addLog('Escaneando colecciones asesor y asociado...');

    try {
      // Load all members
      const allDocs: MemberDoc[] = [];

      const asesorSnap = await getDocs(collection(db, 'asesor'));
      asesorSnap.forEach(d => {
        const data = d.data();
        allDocs.push({
          docId: d.id,
          collection: 'asesor',
          nombre: data.nombre || '',
          correo: (data.correo || '').toLowerCase().trim(),
          miembro_oficial: data.miembro_oficial,
          firebase_uid: data.firebase_uid,
          hasUid: !!(data.firebase_uid && data.firebase_uid.length >= 20),
          propsLinked: 0,
          createdAt: data.fecha_registro || data.fecha_creacion,
        });
      });

      const asociadoSnap = await getDocs(collection(db, 'asociado'));
      asociadoSnap.forEach(d => {
        const data = d.data();
        allDocs.push({
          docId: d.id,
          collection: 'asociado',
          nombre: data.nombre || '',
          correo: (data.correo || '').toLowerCase().trim(),
          miembro_oficial: data.miembro_oficial,
          firebase_uid: data.firebase_uid,
          hasUid: !!(data.firebase_uid && data.firebase_uid.length >= 20),
          propsLinked: 0,
          createdAt: data.fecha_registro || data.fecha_creacion,
        });
      });

      addLog(`${allDocs.length} documentos cargados (${asesorSnap.size} asesores, ${asociadoSnap.size} asociados)`);

      // Count property links per member
      const propSnap = await getDocs(collection(db, 'propiedades'));
      propSnap.forEach(d => {
        const data = d.data();
        const ids: string[] = Array.isArray(data.asesor_doc_ids) ? data.asesor_doc_ids : [];
        ids.forEach(id => {
          const member = allDocs.find(m => m.docId === id);
          if (member) member.propsLinked++;
        });
      });

      // Group by correo
      const byCorreo = new Map<string, MemberDoc[]>();
      for (const d of allDocs) {
        if (!d.correo) continue;
        const existing = byCorreo.get(d.correo) || [];
        existing.push(d);
        byCorreo.set(d.correo, existing);
      }

      // Find duplicates (correo with more than 1 doc)
      const groups: DuplicateGroup[] = [];
      byCorreo.forEach((docs, correo) => {
        if (docs.length > 1) {
          // Auto-select which to keep: prefer hasUid > miembro_oficial > propsLinked > oldest
          const sorted = [...docs].sort((a, b) => {
            if (a.hasUid !== b.hasUid) return a.hasUid ? -1 : 1;
            if (a.miembro_oficial !== b.miembro_oficial) return a.miembro_oficial ? -1 : 1;
            if (a.propsLinked !== b.propsLinked) return b.propsLinked - a.propsLinked;
            return 0;
          });

          groups.push({
            correo,
            docs: sorted,
            keepDocId: sorted[0].docId,
          });
        }
      });

      groups.sort((a, b) => b.docs.length - a.docs.length);
      setDuplicates(groups);
      setScanned(true);

      addLog(`Encontrados ${groups.length} correos duplicados (${groups.reduce((s, g) => s + g.docs.length - 1, 0)} documentos a eliminar)`);
    } catch (err: any) {
      addLog(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // ============ DELETE DUPLICATES ============

  const eliminarDuplicados = async () => {
    // Sprint 3 (F5E-006): confirmación robusta tipeando texto.
    const totalToDelete = duplicates.reduce((s, g) => s + (g.docs.length - 1), 0);
    const ok = await confirmDestructive({
      title: `Eliminar ${totalToDelete} duplicados`,
      message: 'Esta acción es irreversible. Se creará un backup automático antes de proceder.',
      confirmText: 'ELIMINAR-DUPLICADOS',
    });
    if (!ok) return;

    setDeleting(true);
    let deleted = 0;
    let errors = 0;

    addLog(`Iniciando eliminación de duplicados...`);

    // Sprint 3 (F5E-003): backup pre-operación.
    try {
      const toDelete = duplicates.flatMap(g =>
        g.docs.filter(d => d.docId !== g.keepDocId).map(d => ({ collection: d.collection, docId: d.docId }))
      );
      const backupId = await snapshotCollectionDocs('_multi', toDelete.map(d => d.docId), {
        operation: 'limpiar-duplicados',
        userId: (user as any)?.id,
        userEmail: (user as any)?.correo,
        userRol: (user as any)?.rol,
        metadata: { groupCount: duplicates.length, docsAfectados: toDelete.length },
      });
      addLog(`✅ Backup creado: ${backupId}`);
    } catch (backupErr: any) {
      addLog(`❌ Backup falló: ${backupErr.message}. Operación abortada.`);
      setDeleting(false);
      return;
    }

    for (const group of duplicates) {
      for (const d of group.docs) {
        if (d.docId === group.keepDocId) continue; // Skip the one we keep

        try {
          // Sprint 3: hardDelete incluye audit log automático.
          await hardDelete(d.collection, d.docId, {
            userId: (user as any)?.id,
            userEmail: (user as any)?.correo,
            userRol: (user as any)?.rol,
            reason: 'limpiar-duplicados',
            before: d,
          });
          deleted++;
        } catch (err: any) {
          addLog(`Error eliminando ${d.nombre} (${d.docId}): ${err.message}`);
          errors++;
        }
      }
    }

    addLog(`${deleted} duplicados eliminados, ${errors} errores`);

    // Re-propagate miembro_oficial to properties
    addLog('Re-propagando miembro_oficial a propiedades...');
    const result = await propagarMiembroOficial((msg) => addLog(msg));
    addLog(`Propagación: ${result.marcadasOficial} oficiales, ${result.marcadasNoOficial} no oficiales`);

    setDuplicates([]);
    setDeleting(false);
  };

  const changeKeep = (groupIdx: number, docId: string) => {
    setDuplicates(prev => prev.map((g, i) =>
      i === groupIdx ? { ...g, keepDocId: docId } : g
    ));
  };

  // Stats
  const totalDupes = duplicates.reduce((s, g) => s + g.docs.length - 1, 0);

  const isAdmin = user?.rol === 'ADMIN' || user?.tipo?.includes?.('admin');
  if (!isAdmin) {
    return <div className="container py-5"><h3>Acceso no autorizado</h3></div>;
  }

  return (
    <>
      <UserNav />
      <Breadcrumb items={[
        { label: 'Admin', path: '/admin-dashboard' },
        { label: 'Limpiar Duplicados' }
      ]} />

      <section className="py-4">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-11">

              <h4 className="mb-3">
                <i className="fa-solid fa-broom me-2 text-warning"></i>
                Limpiar Duplicados de Miembros
              </h4>

              <p className="text-muted">
                Busca miembros con el mismo correo en más de un documento.
                Selecciona cuál mantener y elimina los duplicados.
              </p>

              {/* Scan button */}
              <button
                className="btn btn-primary mb-4"
                onClick={buscarDuplicados}
                disabled={loading}
              >
                {loading ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span>Escaneando...</>
                ) : (
                  <><i className="fa-solid fa-magnifying-glass me-2"></i>Buscar Duplicados</>
                )}
              </button>

              {/* Results */}
              {scanned && duplicates.length === 0 && (
                <div className="alert alert-success">
                  <i className="fa-solid fa-check-circle me-2"></i>
                  No se encontraron duplicados. La base de datos está limpia.
                </div>
              )}

              {duplicates.length > 0 && (
                <>
                  <div className="alert alert-warning">
                    <strong>{duplicates.length} correos duplicados</strong> encontrados ({totalDupes} documentos a eliminar).
                    <br />
                    <small>El documento marcado con el radio button se mantiene. Los demás se eliminan.</small>
                  </div>

                  {duplicates.map((group, gi) => (
                    <div key={group.correo} className="card mb-3 border-warning">
                      <div className="card-header bg-warning bg-opacity-10 py-2">
                        <strong>{group.correo}</strong>
                        <span className="badge bg-danger ms-2">{group.docs.length} docs</span>
                      </div>
                      <div className="card-body p-0">
                        <table aria-label="Tabla de Limpiar Duplicados" className="table table-sm mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: 40 }}>Mantener</th>
                              <th scope="col">Nombre</th>
                              <th scope="col">Colección</th>
                              <th scope="col">Oficial</th>
                              <th scope="col">Auth UID</th>
                              <th scope="col">Props</th>
                              <th scope="col">DocId</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.docs.map(d => (
                              <tr
                                key={d.docId}
                                className={d.docId === group.keepDocId ? 'table-success' : 'table-danger bg-opacity-10'}
                              >
                                <td>
                                  <input
                                    type="radio"
                                    name={`keep-${group.correo}`}
                                    checked={d.docId === group.keepDocId}
                                    onChange={() => changeKeep(gi, d.docId)}
                                  />
                                </td>
                                <td>{d.nombre}</td>
                                <td>
                                  <span className={`badge ${d.collection === 'asesor' ? 'bg-info' : 'bg-secondary'}`}>
                                    {d.collection}
                                  </span>
                                </td>
                                <td>
                                  {d.miembro_oficial ? (
                                    <span className="badge bg-success">Oficial</span>
                                  ) : (
                                    <span className="badge bg-light text-dark">No</span>
                                  )}
                                </td>
                                <td>
                                  {d.hasUid ? (
                                    <span className="badge bg-success">Si</span>
                                  ) : (
                                    <span className="badge bg-light text-dark">No</span>
                                  )}
                                </td>
                                <td>{d.propsLinked}</td>
                                <td><small className="text-muted">{d.docId.slice(0, 12)}...</small></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}

                  <button
                    className="btn btn-danger btn-lg"
                    onClick={eliminarDuplicados}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Eliminando...</>
                    ) : (
                      <><i className="fa-solid fa-trash me-2"></i>Eliminar {totalDupes} duplicados</>
                    )}
                  </button>
                </>
              )}

              {/* Log */}
              {log.length > 0 && (
                <div className="card mt-4 border-0 bg-dark text-light">
                  <div className="card-header bg-dark border-bottom border-secondary">
                    <small className="text-muted">Log</small>
                  </div>
                  <div className="card-body p-3" style={{ maxHeight: '200px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {log.map((msg, i) => <div key={i}>{msg}</div>)}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="" />
      <Footer />
    </>
  );
}
